import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Chip,
  Checkbox,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  FormControl,
  Grid,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Slider,
  TextField,
  Typography,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DownloadIcon from "@mui/icons-material/Download";
import ContentCutIcon from "@mui/icons-material/ContentCut";
import DeleteIcon from "@mui/icons-material/Delete";
import PublishIcon from "@mui/icons-material/Publish";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import LaunchIcon from "@mui/icons-material/Launch";
import { useAuth } from "../hooks/useAuth";
import { useToastStateSync } from "../hooks/useToastStateSync";
import AppService from "../services/AppService";
import AIService from "../services/AIService";
import IconService from "../services/IconService";
import type { App } from "../types/app";
import type {
  CreateIconDTO,
  GeneratedIcon,
  GeneratedIconVariant,
  GooglePlayIconPublishResponse,
  GooglePlayIconValidationResponse,
  IconCropSettings,
  IconMood,
  IconPlatform,
  IconStyle,
} from "../types/icon";

const STYLES: IconStyle[] = [
  "flat",
  "gradient",
  "3d",
  "minimalist",
  "illustrative",
  "geometric",
];
const MOODS: IconMood[] = [
  "professional",
  "playful",
  "elegant",
  "bold",
  "calm",
];
const STYLE_MODES = [
  { value: "manual", label: "Manual Style" },
  { value: "ai", label: "AI Style Mode" },
] as const;
const IMAGE_MODELS = [
  { value: "gemini-3-pro-image-preview", label: "Gemini 3 Pro Image Preview" },
  {
    value: "gemini-2.5-flash-image",
    label: "Gemini 2.5 Flash Image (Nano Banana)",
  },
  { value: "gpt-image-2.5-flare", label: "OpenAI GPT Image 2.5 Flare" },
  { value: "gpt-image-2.5-sunburst", label: "OpenAI GPT Image 2.5 Sunburst" },
];

export default function AppIconGenerator() {
  const { user } = useAuth();
  const [apps, setApps] = useState<App[]>([]);
  const [selectedApp, setSelectedApp] = useState<App | null>(null);
  const [generatedIcons, setGeneratedIcons] = useState<GeneratedIcon[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  useToastStateSync({ success, error });

  const [style, setStyle] = useState<IconStyle>("flat");
  const [mood, setMood] = useState<IconMood>("professional");
  const [styleMode, setStyleMode] = useState<"manual" | "ai">("ai");
  const [targetPlatform, setTargetPlatform] = useState<IconPlatform>("ios");
  const [imageModel, setImageModel] = useState<string>(
    "gpt-image-2.5-flare",
  );
  const [primaryColor, setPrimaryColor] = useState("");
  const [secondaryColor, setSecondaryColor] = useState("");
  const [keywordsInput, setKeywordsInput] = useState("");
  const [keywords, setKeywords] = useState<string[]>([]);
  const [symbolsInput, setSymbolsInput] = useState("");
  const [symbols, setSymbols] = useState<string[]>([]);
  const [avoidInput, setAvoidInput] = useState("");
  const [avoidElements, setAvoidElements] = useState<string[]>([]);
  const [customPrompt, setCustomPrompt] = useState("");
  const [aiStylePromptDraft, setAiStylePromptDraft] = useState("");
  const [aiSuggestedStyle, setAiSuggestedStyle] =
    useState<IconStyle>("minimalist");
  const [aiSuggestedMood, setAiSuggestedMood] =
    useState<IconMood>("professional");
  const [generatingAIStyle, setGeneratingAIStyle] = useState(false);

  const [cropDialogOpen, setCropDialogOpen] = useState(false);
  const [selectedIcon, setSelectedIcon] = useState<GeneratedIcon | null>(null);
  const [previewImage, setPreviewImage] = useState<HTMLImageElement | null>(
    null,
  );
  const [cropPlatform, setCropPlatform] = useState<IconPlatform>("both");
  const [cropSettings, setCropSettings] = useState<IconCropSettings>({
    offsetX: 0,
    offsetY: 0,
    zoom: 1,
  });
  const [variants, setVariants] = useState<GeneratedIconVariant[]>([]);
  const [processingVariants, setProcessingVariants] = useState(false);
  const [publishDialogOpen, setPublishDialogOpen] = useState(false);
  const [publishTargetIcon, setPublishTargetIcon] = useState<GeneratedIcon | null>(
    null,
  );
  const [validatingGooglePlayIcon, setValidatingGooglePlayIcon] =
    useState(false);
  const [publishingGooglePlayIcon, setPublishingGooglePlayIcon] =
    useState(false);
  const [publishProgress, setPublishProgress] = useState(0);
  const [publishProgressLabel, setPublishProgressLabel] = useState("");
  const [publishConfirmed, setPublishConfirmed] = useState(false);
  const [iconValidationResult, setIconValidationResult] =
    useState<GooglePlayIconValidationResponse | null>(null);
  const [iconPublishResult, setIconPublishResult] =
    useState<GooglePlayIconPublishResponse | null>(null);
  const [publishView, setPublishView] = useState<
    "form" | "progress" | "success"
  >("form");
  const cropPreviewCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const cropPreviewMetricsRef = useRef<{
    maxShiftX: number;
    maxShiftY: number;
  } | null>(null);
  const cropDragStateRef = useRef<{
    pointerId: number | null;
    startX: number;
    startY: number;
    startOffsetX: number;
    startOffsetY: number;
    active: boolean;
  }>({
    pointerId: null,
    startX: 0,
    startY: 0,
    startOffsetX: 0,
    startOffsetY: 0,
    active: false,
  });
  const [isDraggingCropPreview, setIsDraggingCropPreview] = useState(false);

  const parseJson = <T,>(text: string): T | null => {
    const raw = String(text || "").trim();
    if (!raw) return null;
    try {
      return JSON.parse(raw) as T;
    } catch {
      // continue
    }
    const fenced =
      raw.match(/```json\s*([\s\S]*?)\s*```/i)?.[1] ||
      raw.match(/```\s*([\s\S]*?)\s*```/i)?.[1];
    if (fenced) {
      try {
        return JSON.parse(fenced) as T;
      } catch {
        // continue
      }
    }
    const start = raw.indexOf("{");
    const end = raw.lastIndexOf("}");
    if (start >= 0 && end > start) {
      try {
        return JSON.parse(raw.slice(start, end + 1)) as T;
      } catch {
        return null;
      }
    }
    return null;
  };

  const resolveIconStyle = (value: string | undefined): IconStyle => {
    const normalized = String(value || "").toLowerCase() as IconStyle;
    return STYLES.includes(normalized) ? normalized : "minimalist";
  };

  const resolveIconMood = (value: string | undefined): IconMood => {
    const normalized = String(value || "").toLowerCase() as IconMood;
    return MOODS.includes(normalized) ? normalized : "professional";
  };

  const loadApps = useCallback(async () => {
    if (!user?.email) return;
    try {
      const userApps = await AppService.getUserApps(user.email);
      setApps(userApps);
    } catch (loadError) {
      console.error(loadError);
      setError("Failed to load registered apps.");
    }
  }, [user?.email]);

  const loadGeneratedIcons = useCallback(async () => {
    if (!selectedApp || !user?.email) return;
    try {
      const icons = await IconService.getIconsByAppId(
        selectedApp.appId,
        user.email,
      );
      setGeneratedIcons(icons);
    } catch (loadError) {
      console.error(loadError);
      setError("Failed to load app icons.");
    }
  }, [selectedApp, user?.email]);

  useEffect(() => {
    loadApps();
  }, [loadApps]);

  useEffect(() => {
    loadGeneratedIcons();
  }, [loadGeneratedIcons]);

  const addChip = (
    value: string,
    setValue: React.Dispatch<React.SetStateAction<string>>,
    list: string[],
    setList: React.Dispatch<React.SetStateAction<string[]>>,
  ) => {
    const next = value.trim();
    if (!next) return;
    if (!list.includes(next)) {
      setList([...list, next]);
    }
    setValue("");
  };

  const removeChip = (
    chip: string,
    setList: React.Dispatch<React.SetStateAction<string[]>>,
  ) => {
    setList((previous) => previous.filter((item) => item !== chip));
  };

  const handleGenerateIcon = async () => {
    if (!selectedApp || !user?.email) {
      setError("Select an app and sign in first.");
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      if (styleMode === "ai" && !aiStylePromptDraft.trim()) {
        throw new Error(
          "Generate app icon style first (or enter style prompt) before generating icon.",
        );
      }

      const request: CreateIconDTO = {
        appId: selectedApp.appId,
        appName: selectedApp.name,
        appDescription:
          styleMode === "ai"
            ? `${selectedApp.description || ""}`.trim()
            : `${selectedApp.description || ""}\n${
                customPrompt ? `Extra direction: ${customPrompt}` : ""
              }`.trim(),
        appCategory: selectedApp.category,
        appPlatform: selectedApp.platform,
        appStoreUrl: selectedApp.storeUrl,
        androidStoreUrl: selectedApp.androidStoreUrl,
        iosStoreUrl: selectedApp.iosStoreUrl,
        researchContext: selectedApp.researchContext,
        style: styleMode === "ai" ? aiSuggestedStyle : style,
        mood: styleMode === "ai" ? aiSuggestedMood : mood,
        styleMode,
        aiStylePrompt:
          styleMode === "ai" ? aiStylePromptDraft.trim() : undefined,
        aiSuggestedStyle,
        aiSuggestedMood,
        colorScheme:
          styleMode === "ai"
            ? {}
            : {
                ...(primaryColor.trim()
                  ? { primary: primaryColor.trim() }
                  : {}),
                ...(secondaryColor.trim()
                  ? { secondary: secondaryColor.trim() }
                  : {}),
              },
        platform: targetPlatform,
        targetPlatform,
        keywords: styleMode === "ai" ? [] : keywords,
        symbols: styleMode === "ai" ? [] : symbols,
        avoidElements: styleMode === "ai" ? [] : avoidElements,
        model: imageModel,
      };

      const createdIcons = await IconService.generateIcon(user.email, request);
      setGeneratedIcons((prev) => [...createdIcons, ...prev]);
      setSuccess(
        styleMode === "ai"
          ? `Generated ${createdIcons.length} icon(s) using AI style direction.`
          : `Generated ${createdIcons.length} icon(s).`,
      );
    } catch (generationError: any) {
      console.error(generationError);
      setError(generationError?.message || "Failed to generate icon.");
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateAIStyle = async () => {
    if (!selectedApp || !user?.email) {
      setError("Select an app and sign in first.");
      return;
    }

    try {
      setGeneratingAIStyle(true);
      setError(null);
      setSuccess(null);

      const stylePrompt = [
        "You are an elite app icon creative director.",
        "Generate a highly detailed icon style brief that is engineered for high tap-through and instant recognition at small sizes.",
        "The style brief must be vivid, specific, and production-ready for image generation.",
        "Do not be generic. Avoid short summaries.",
        "",
        "Quality requirements:",
        "- One dominant focal concept.",
        "- Strong high-contrast silhouette and clear metaphor.",
        "- 60px readability and squint-test clarity.",
        "- Full-bleed square composition with hard square corners (no rounded corners baked in).",
        "- No text, no logos from other brands, no watermark.",
        "",
        `App name: ${selectedApp.name}`,
        `Category: ${selectedApp.category || "general"}`,
        `Platform target: ${targetPlatform}`,
        `Description: ${selectedApp.description || "N/A"}`,
        `Research context: ${
          selectedApp.researchContext?.whatItDoes ||
          selectedApp.researchContext?.summary ||
          "N/A"
        }`,
        "",
        "Return JSON only with this exact shape:",
        '{ "style": "flat|gradient|3d|minimalist|illustrative|geometric", "mood": "professional|playful|elegant|bold|calm", "stylePrompt": "multi-section icon prompt brief" }',
        "",
        "The stylePrompt must be 180-320 words and follow this structure exactly:",
        "Headline: one sentence visual concept.",
        "Core concept: 2-3 short lines describing the reveal or focal metaphor.",
        "Visual execution ideas: exactly 3 bullet-style lines, each a distinct execution path.",
        "Design details: specific subject details and realism/stylization cues.",
        "Hook element: what creates curiosity instantly.",
        "Style: rendering direction (lighting, depth, finish).",
        "Color palette: base and accent guidance with contrast intent.",
        "Background: non-distracting background direction.",
        "Composition: placement and hierarchy for small-size clarity.",
        "Emotional trigger: 2-3 words/phrases.",
        "Constraints: concise list (no text, no clutter, no split-before-after gimmick, no cartoon unless app style demands it).",
        "Keywords for model: comma-separated terms.",
      ].join("\n");

      const styleResult = await AIService.generateJSON<{
        style?: string;
        mood?: string;
        stylePrompt?: string;
      }>(stylePrompt, {
        provider: "openai",
        model: "gpt-4.1",
        structuredOutputName: "icon_style_brief",
        structuredOutputSchema: {
          type: "object",
          properties: {
            style: { type: "string" },
            mood: { type: "string" },
            stylePrompt: { type: "string" },
          },
          required: ["style", "mood", "stylePrompt"],
        },
        temperature: 0.6,
        maxTokens: 500,
      });

      let nextStyle = resolveIconStyle(styleResult.style);
      let nextMood = resolveIconMood(styleResult.mood);
      let nextPrompt = String(styleResult.stylePrompt || "").trim();

      if (!nextPrompt) {
        const fallback = await AIService.generateText(stylePrompt, {
          provider: "openai",
          model: "gpt-4.1",
          temperature: 0.6,
          maxTokens: 500,
        });
        const parsed = parseJson<{
          style?: string;
          mood?: string;
          stylePrompt?: string;
        }>(fallback.text);
        nextStyle = resolveIconStyle(parsed?.style || nextStyle);
        nextMood = resolveIconMood(parsed?.mood || nextMood);
        nextPrompt = String(parsed?.stylePrompt || "").trim();
      }

      if (!nextPrompt) {
        nextPrompt =
          [
            "A premium, high-contrast app icon with one dominant central metaphor and instant small-size recognition.",
            "",
            "Core concept:",
            "A clear single-subject mark that communicates the app benefit immediately.",
            "",
            "Visual execution ideas:",
            "- Dramatic foreground subject with subtle depth lighting.",
            "- Clean geometric support shape behind the subject.",
            "- Controlled glow accent to guide eye focus.",
            "",
            "Design details:",
            "Crisp edges, balanced symmetry, subtle depth, no micro-noise.",
            "",
            "Hook element:",
            "One curiosity-driving highlight near the transformation/focus area.",
            "",
            "Style:",
            "Hyper-clean premium render, cinematic lighting, glossy finish.",
            "",
            "Color palette:",
            "Dark base with one bright accent and one support mid-tone.",
            "",
            "Background:",
            "Smooth gradient with soft vignette, no distractions.",
            "",
            "Composition:",
            "Centered dominant subject, safe margins, clear at 60px.",
            "",
            "Emotional trigger:",
            "Curiosity, confidence, intrigue.",
            "",
            "Constraints:",
            "No text. No clutter. No watermark. No rounded-corner frame baked into image.",
            "",
            "Keywords for model:",
            "premium app icon, high contrast, cinematic lighting, clean silhouette, curiosity hook",
          ].join("\n");
      }

      setAiSuggestedStyle(nextStyle);
      setAiSuggestedMood(nextMood);
      setAiStylePromptDraft(nextPrompt);
      setSuccess(
        "AI icon style generated. You can edit it before generating icon.",
      );
    } catch (styleError: any) {
      console.error(styleError);
      setError(styleError?.message || "Failed to generate AI icon style.");
    } finally {
      setGeneratingAIStyle(false);
    }
  };

  const openCropDialog = (icon: GeneratedIcon) => {
    setSelectedIcon(icon);
    setCropPlatform("both");
    setCropSettings({
      offsetX: 0,
      offsetY: 0,
      zoom: 1,
    });
    setVariants([]);
    setCropDialogOpen(true);
  };

  const handleGenerateVariants = async () => {
    if (!selectedIcon) return;
    setProcessingVariants(true);
    setError(null);
    try {
      const generated = await IconService.generateIconVariants(
        selectedIcon,
        cropSettings,
        cropPlatform,
      );
      setVariants(generated);
      setSuccess(`Generated ${generated.length} icon variants.`);
    } catch (generationError: any) {
      console.error(generationError);
      setError(
        generationError?.message ||
          "Failed to crop and create variants. Ensure the icon URL is accessible for canvas processing.",
      );
    } finally {
      setProcessingVariants(false);
    }
  };

  const handleDownloadVariants = async () => {
    if (!selectedApp || variants.length === 0) return;
    await IconService.downloadVariants(variants, selectedApp.name);
    setSuccess(`Downloading ${variants.length} icon files.`);
  };

  const handleDeleteIcon = async (icon: GeneratedIcon) => {
    if (!user?.email) return;
    if (!window.confirm("Delete this icon record?")) return;
    try {
      await IconService.deleteIcon(icon.iconId, user.email);
      setGeneratedIcons((prev) =>
        prev.filter((item) => item.iconId !== icon.iconId),
      );
    } catch (deleteError) {
      console.error(deleteError);
      setError("Failed to delete icon.");
    }
  };

  const openPublishDialog = (icon: GeneratedIcon) => {
    setPublishTargetIcon(icon);
    setPublishDialogOpen(true);
    setPublishConfirmed(false);
    setIconValidationResult(null);
    setIconPublishResult(null);
    setPublishProgress(0);
    setPublishProgressLabel("");
    setPublishView("form");
    setError(null);
    setSuccess(null);
  };

  const closePublishDialog = () => {
    if (publishingGooglePlayIcon) return;
    setPublishDialogOpen(false);
    setPublishTargetIcon(null);
    setPublishConfirmed(false);
    setIconValidationResult(null);
    setIconPublishResult(null);
    setPublishProgress(0);
    setPublishProgressLabel("");
    setPublishView("form");
  };

  const handleValidateGooglePlayIcon = async () => {
    if (!selectedApp || !publishTargetIcon) {
      setError("Select an app icon before validating.");
      return;
    }

    try {
      setValidatingGooglePlayIcon(true);
      setError(null);
      setIconPublishResult(null);
      const response = await IconService.validateGooglePlayIcon({
        appId: selectedApp.appId,
        iconId: publishTargetIcon.iconId,
      });
      setIconValidationResult(response);
      if (response.success) {
        setSuccess(response.message || "Google Play icon validation completed.");
      } else {
        setError(response.message || "Icon validation found issues.");
      }
    } catch (validationError: any) {
      console.error(validationError);
      const responseData =
        validationError?.responseData as GooglePlayIconValidationResponse;
      if (responseData?.packageName) {
        setIconValidationResult(responseData);
      }
      setError(
        responseData?.message ||
          validationError?.message ||
          "Failed to validate Google Play icon.",
      );
    } finally {
      setValidatingGooglePlayIcon(false);
    }
  };

  const handlePublishGooglePlayIcon = async () => {
    if (!selectedApp || !publishTargetIcon) {
      setError("Select an app icon before publishing.");
      return;
    }

    let progressTimer: number | null = null;
    let stageTimer: number | null = null;

    try {
      setPublishingGooglePlayIcon(true);
      setPublishView("progress");
      setPublishProgress(18);
      setPublishProgressLabel("Preparing Google Play icon publish...");
      setError(null);
      setSuccess(null);

      progressTimer = window.setInterval(() => {
        setPublishProgress((prev) => (prev >= 92 ? prev : prev + 6));
      }, 350);

      stageTimer = window.setTimeout(() => {
        setPublishProgressLabel("Uploading icon and committing Play edit...");
      }, 800);

      const response = await IconService.publishGooglePlayIcon({
        appId: selectedApp.appId,
        iconId: publishTargetIcon.iconId,
      });

      setPublishProgress(100);
      setPublishProgressLabel("Publish complete.");
      setIconPublishResult(response);
      setPublishView("success");
      setSuccess(response.message || "Icon published to Google Play.");
    } catch (publishError: any) {
      console.error(publishError);
      setPublishView("form");
      const responseData =
        publishError?.responseData as GooglePlayIconValidationResponse;
      if (responseData?.packageName) {
        setIconValidationResult(responseData);
      }
      setError(
        publishError?.message || "Failed to publish icon to Google Play.",
      );
    } finally {
      if (progressTimer !== null) {
        window.clearInterval(progressTimer);
      }
      if (stageTimer !== null) {
        window.clearTimeout(stageTimer);
      }
      setPublishingGooglePlayIcon(false);
    }
  };

  const handleCropPreviewPointerDown = (
    event: React.PointerEvent<HTMLCanvasElement>,
  ) => {
    cropDragStateRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      startOffsetX: cropSettings.offsetX,
      startOffsetY: cropSettings.offsetY,
      active: true,
    };
    setIsDraggingCropPreview(true);
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const handleCropPreviewPointerMove = (
    event: React.PointerEvent<HTMLCanvasElement>,
  ) => {
    const dragState = cropDragStateRef.current;
    const metrics = cropPreviewMetricsRef.current;
    if (
      !dragState.active ||
      dragState.pointerId !== event.pointerId ||
      !metrics
    ) {
      return;
    }

    const clamp = (value: number, min: number, max: number) =>
      Math.min(Math.max(value, min), max);
    const rect = event.currentTarget.getBoundingClientRect();
    const scaleX = rect.width > 0 ? 1024 / rect.width : 1;
    const scaleY = rect.height > 0 ? 1024 / rect.height : 1;
    const dx = (event.clientX - dragState.startX) * scaleX;
    const dy = (event.clientY - dragState.startY) * scaleY;

    const deltaOffsetX =
      metrics.maxShiftX > 0 ? -(dx / metrics.maxShiftX) * 100 : 0;
    const deltaOffsetY =
      metrics.maxShiftY > 0 ? -(dy / metrics.maxShiftY) * 100 : 0;

    setCropSettings((previous) => ({
      ...previous,
      offsetX: clamp(dragState.startOffsetX + deltaOffsetX, -100, 100),
      offsetY: clamp(dragState.startOffsetY + deltaOffsetY, -100, 100),
    }));
  };

  const handleCropPreviewPointerEnd = (
    event: React.PointerEvent<HTMLCanvasElement>,
  ) => {
    const dragState = cropDragStateRef.current;
    if (dragState.active && dragState.pointerId === event.pointerId) {
      cropDragStateRef.current = {
        pointerId: null,
        startX: 0,
        startY: 0,
        startOffsetX: 0,
        startOffsetY: 0,
        active: false,
      };
      setIsDraggingCropPreview(false);
      try {
        event.currentTarget.releasePointerCapture(event.pointerId);
      } catch {
        // no-op
      }
    }
  };

  useEffect(() => {
    if (!cropDialogOpen || !selectedIcon?.baseImageUrl) {
      return;
    }
    const image = new Image();
    image.onload = () => setPreviewImage(image);
    image.onerror = () => setPreviewImage(null);
    image.src = selectedIcon.baseImageUrl;
  }, [cropDialogOpen, selectedIcon?.baseImageUrl]);

  useEffect(() => {
    if (!cropDialogOpen || !previewImage || !cropPreviewCanvasRef.current) {
      return;
    }

    const clamp = (value: number, min: number, max: number) =>
      Math.min(Math.max(value, min), max);
    const outputSize = 1024;
    const zoom = clamp(Number(cropSettings.zoom) || 1, 1, 3);
    const offsetX = clamp(Number(cropSettings.offsetX) || 0, -100, 100);
    const offsetY = clamp(Number(cropSettings.offsetY) || 0, -100, 100);

    const sourceWidth = previewImage.naturalWidth || outputSize;
    const sourceHeight = previewImage.naturalHeight || outputSize;
    const baseScale = Math.max(
      outputSize / sourceWidth,
      outputSize / sourceHeight,
    );
    const drawScale = baseScale * zoom;
    const drawWidth = Math.round(sourceWidth * drawScale);
    const drawHeight = Math.round(sourceHeight * drawScale);
    const maxShiftX = Math.max(0, (drawWidth - outputSize) / 2);
    const maxShiftY = Math.max(0, (drawHeight - outputSize) / 2);
    cropPreviewMetricsRef.current = { maxShiftX, maxShiftY };

    const left = Math.round(
      (drawWidth - outputSize) / 2 + (offsetX / 100) * maxShiftX,
    );
    const top = Math.round(
      (drawHeight - outputSize) / 2 + (offsetY / 100) * maxShiftY,
    );
    const safeLeft = clamp(left, 0, Math.max(0, drawWidth - outputSize));
    const safeTop = clamp(top, 0, Math.max(0, drawHeight - outputSize));

    const offscreen = document.createElement("canvas");
    offscreen.width = drawWidth;
    offscreen.height = drawHeight;
    const offscreenCtx = offscreen.getContext("2d");
    if (!offscreenCtx) return;
    offscreenCtx.imageSmoothingEnabled = true;
    offscreenCtx.imageSmoothingQuality = "high";
    offscreenCtx.drawImage(previewImage, 0, 0, drawWidth, drawHeight);

    const canvas = cropPreviewCanvasRef.current;
    if (!canvas) return;
    canvas.width = outputSize;
    canvas.height = outputSize;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.clearRect(0, 0, outputSize, outputSize);
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";
    ctx.drawImage(
      offscreen,
      safeLeft,
      safeTop,
      outputSize,
      outputSize,
      0,
      0,
      outputSize,
      outputSize,
    );
  }, [cropDialogOpen, previewImage, cropSettings]);

  const publishBlockers: string[] = [];
  const selectedAppPackageName = String(selectedApp?.googlePlayPackageName || "").trim();
  if (!selectedApp) {
    publishBlockers.push("Select a registered app first.");
  } else if (!selectedAppPackageName) {
    publishBlockers.push(
      "Add a Google Play package name in App Registration before publishing an icon.",
    );
  }
  if (!publishTargetIcon) {
    publishBlockers.push("Select an icon to publish.");
  }

  const hasIconValidationErrors = Boolean(
    iconValidationResult?.validationIssues?.some(
      (issue) => issue.severity === "error",
    ),
  );
  const canPublishIcon =
    publishBlockers.length === 0 &&
    publishConfirmed &&
    Boolean(iconValidationResult) &&
    !hasIconValidationErrors &&
    !publishingGooglePlayIcon;

  return (
    <Container maxWidth={false} sx={{ py: 4 }}>
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
        App Icon Generator
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Generate icons with registered app context, then crop and export all
        required iOS/Android icon sizes.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert
          severity="success"
          sx={{ mb: 2 }}
          onClose={() => setSuccess(null)}
        >
          {success}
        </Alert>
      )}

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Select a registered app</InputLabel>
              <Select
                value={selectedApp?.appId || ""}
                label="Select a registered app"
                onChange={(event: SelectChangeEvent) => {
                  const app =
                    apps.find(
                      (candidate) => candidate.appId === event.target.value,
                    ) || null;
                  setSelectedApp(app);
                  if (app?.platform === "android") {
                    setTargetPlatform("android");
                  } else if (app?.platform === "both") {
                    setTargetPlatform("both");
                  } else {
                    setTargetPlatform("ios");
                  }
                }}
              >
                <MenuItem value="">
                  <em>Select a registered app</em>
                </MenuItem>
                {apps.map((app) => (
                  <MenuItem key={app.appId} value={app.appId}>
                    {app.name} ({app.platform})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {selectedApp && (
              <Box sx={{ mt: 2 }}>
                <Chip
                  label={`Category: ${selectedApp.category}`}
                  size="small"
                  sx={{ mr: 1 }}
                />
                <Chip
                  label={`Platform: ${selectedApp.platform}`}
                  size="small"
                />
              </Box>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Icon Prompt Inputs
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Style Mode</InputLabel>
                  <Select
                    value={styleMode}
                    label="Style Mode"
                    onChange={(e) =>
                      setStyleMode(e.target.value as "manual" | "ai")
                    }
                  >
                    {STYLE_MODES.map((mode) => (
                      <MenuItem key={mode.value} value={mode.value}>
                        {mode.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              {styleMode === "ai" && (
                <>
                  <Grid item xs={12}>
                    <Button
                      variant="outlined"
                      onClick={handleGenerateAIStyle}
                      disabled={generatingAIStyle || !selectedApp}
                      startIcon={
                        generatingAIStyle ? (
                          <CircularProgress size={16} color="inherit" />
                        ) : (
                          <CloudUploadIcon />
                        )
                      }
                    >
                      {generatingAIStyle
                        ? "Generating Style..."
                        : "Generate App Icon Style"}
                    </Button>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      multiline
                      minRows={4}
                      label="AI Style Prompt"
                      value={aiStylePromptDraft}
                      onChange={(e) => setAiStylePromptDraft(e.target.value)}
                      placeholder="Generate AI style first, then optionally edit this style direction."
                    />
                    <Typography variant="caption" color="text.secondary">
                      Suggested style: {aiSuggestedStyle} | Suggested mood:{" "}
                      {aiSuggestedMood}
                    </Typography>
                  </Grid>
                </>
              )}
              {styleMode === "manual" && (
                <>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <InputLabel>Style</InputLabel>
                      <Select
                        value={style}
                        label="Style"
                        onChange={(e) => setStyle(e.target.value as IconStyle)}
                      >
                        {STYLES.map((value) => (
                          <MenuItem key={value} value={value}>
                            {value}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <InputLabel>Mood</InputLabel>
                      <Select
                        value={mood}
                        label="Mood"
                        onChange={(e) => setMood(e.target.value as IconMood)}
                      >
                        {MOODS.map((value) => (
                          <MenuItem key={value} value={value}>
                            {value}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                </>
              )}
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Target Platform</InputLabel>
                  <Select
                    value={targetPlatform}
                    label="Target Platform"
                    onChange={(e) =>
                      setTargetPlatform(e.target.value as IconPlatform)
                    }
                  >
                    <MenuItem value="ios">iOS</MenuItem>
                    <MenuItem value="android">Android</MenuItem>
                    <MenuItem value="both">Both</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Image Model</InputLabel>
                  <Select
                    value={imageModel}
                    label="Image Model"
                    onChange={(e) => setImageModel(e.target.value)}
                  >
                    {IMAGE_MODELS.map((model) => (
                      <MenuItem key={model.value} value={model.value}>
                        {model.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              {styleMode === "manual" && (
                <>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      Primary Color
                    </Typography>
                    <TextField
                      fullWidth
                      value={primaryColor}
                      onChange={(e) => setPrimaryColor(e.target.value)}
                      placeholder="#1976D2 (optional)"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      Secondary Color
                    </Typography>
                    <TextField
                      fullWidth
                      value={secondaryColor}
                      onChange={(e) => setSecondaryColor(e.target.value)}
                      placeholder="#DC004E (optional)"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      multiline
                      minRows={2}
                      label="Extra Prompt Direction (Optional)"
                      value={customPrompt}
                      onChange={(e) => setCustomPrompt(e.target.value)}
                    />
                  </Grid>
                </>
              )}
            </Grid>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Design Tokens
            </Typography>
            {styleMode === "ai" ? (
              <Alert severity="info">
                AI Style Mode is active. Manual tokens (keywords, symbols, avoid
                elements) are ignored.
              </Alert>
            ) : (
              <>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2">Keywords</Typography>
                  <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
                    <TextField
                      fullWidth
                      value={keywordsInput}
                      onChange={(e) => setKeywordsInput(e.target.value)}
                    />
                    <Button
                      onClick={() =>
                        addChip(
                          keywordsInput,
                          setKeywordsInput,
                          keywords,
                          setKeywords,
                        )
                      }
                    >
                      Add
                    </Button>
                  </Box>
                  <Box
                    sx={{ mt: 1, display: "flex", gap: 1, flexWrap: "wrap" }}
                  >
                    {keywords.map((value) => (
                      <Chip
                        key={value}
                        label={value}
                        onDelete={() => removeChip(value, setKeywords)}
                      />
                    ))}
                  </Box>
                </Box>

                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2">
                    Symbols to Include
                  </Typography>
                  <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
                    <TextField
                      fullWidth
                      value={symbolsInput}
                      onChange={(e) => setSymbolsInput(e.target.value)}
                    />
                    <Button
                      onClick={() =>
                        addChip(
                          symbolsInput,
                          setSymbolsInput,
                          symbols,
                          setSymbols,
                        )
                      }
                    >
                      Add
                    </Button>
                  </Box>
                  <Box
                    sx={{ mt: 1, display: "flex", gap: 1, flexWrap: "wrap" }}
                  >
                    {symbols.map((value) => (
                      <Chip
                        key={value}
                        label={value}
                        onDelete={() => removeChip(value, setSymbols)}
                      />
                    ))}
                  </Box>
                </Box>

                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2">Avoid Elements</Typography>
                  <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
                    <TextField
                      fullWidth
                      value={avoidInput}
                      onChange={(e) => setAvoidInput(e.target.value)}
                    />
                    <Button
                      onClick={() =>
                        addChip(
                          avoidInput,
                          setAvoidInput,
                          avoidElements,
                          setAvoidElements,
                        )
                      }
                    >
                      Add
                    </Button>
                  </Box>
                  <Box
                    sx={{ mt: 1, display: "flex", gap: 1, flexWrap: "wrap" }}
                  >
                    {avoidElements.map((value) => (
                      <Chip
                        key={value}
                        label={value}
                        color="error"
                        variant="outlined"
                        onDelete={() => removeChip(value, setAvoidElements)}
                      />
                    ))}
                  </Box>
                </Box>
              </>
            )}

            <Button
              fullWidth
              variant="contained"
              startIcon={
                loading ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <CloudUploadIcon />
                )
              }
              onClick={handleGenerateIcon}
              disabled={loading || !selectedApp}
            >
              {loading ? "Generating Icon..." : "Generate Icon"}
            </Button>
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Icons For Selected App ({generatedIcons.length})
            </Typography>
            {generatedIcons.length === 0 ? (
              <Alert severity="info">
                No icons for this app yet. Generate your first icon above.
              </Alert>
            ) : (
              <Grid container spacing={2}>
                {generatedIcons.map((icon) => (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={icon.iconId}>
                    <Card>
                      <CardContent>
                        <Box
                          sx={{
                            width: "100%",
                            aspectRatio: "1/1",
                            borderRadius: 2,
                            bgcolor: "action.hover",
                            overflow: "hidden",
                            mb: 2,
                            border: "1px solid", borderColor: "divider",
                          }}
                        >
                          <img
                            src={icon.baseImageUrl}
                            alt={icon.iconId}
                            style={{
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                            }}
                          />
                        </Box>
                        <Typography variant="body2" color="text.secondary">
                          Style: {icon.style}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Mood: {icon.mood}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Model: {icon.metadata.aiModel}
                        </Typography>
                      </CardContent>
                      <CardActions sx={{ justifyContent: "space-between" }}>
                        <Box
                          sx={{
                            width: "100%",
                            display: "flex",
                            flexDirection: "column",
                            gap: 0.75,
                          }}
                        >
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<PublishIcon />}
                            onClick={() => openPublishDialog(icon)}
                          >
                            Publish to Play
                          </Button>
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              gap: 1,
                            }}
                          >
                            <Button
                              size="small"
                              startIcon={<ContentCutIcon />}
                              onClick={() => openCropDialog(icon)}
                            >
                              Crop & Export
                            </Button>
                            <Button
                              size="small"
                              color="error"
                              startIcon={<DeleteIcon />}
                              onClick={() => handleDeleteIcon(icon)}
                            >
                              Delete
                            </Button>
                          </Box>
                        </Box>
                      </CardActions>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}
          </Paper>
        </Grid>
      </Grid>

      <Dialog
        open={publishDialogOpen}
        onClose={closePublishDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Publish Icon to Google Play</DialogTitle>
        <DialogContent>
          {publishView === "progress" ? (
            <Paper
              variant="outlined"
              sx={{
                mt: 1,
                p: 3,
                borderRadius: 2,
                textAlign: "center",
              }}
            >
              <CircularProgress size={56} sx={{ mb: 2 }} />
              <Typography variant="h6" sx={{ mb: 0.5 }}>
                Publishing in progress
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {publishProgressLabel || "Preparing publish request..."}
              </Typography>
              <LinearProgress
                variant="determinate"
                value={publishProgress}
                sx={{ height: 10, borderRadius: 999, mb: 1 }}
              />
              <Typography variant="caption" color="text.secondary">
                {Math.round(publishProgress)}%
              </Typography>
            </Paper>
          ) : publishView === "success" ? (
            <Paper
              variant="outlined"
              sx={{
                mt: 1,
                p: 3,
                borderRadius: 2,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  mb: 1.5,
                }}
              >
                <CheckCircleIcon color="success" />
                <Typography variant="h6">Icon published successfully</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {iconPublishResult?.message ||
                  "The selected icon is now live in your Google Play draft edit."}
              </Typography>
              {iconPublishResult?.packageName ? (
                <Button
                  variant="outlined"
                  startIcon={<LaunchIcon />}
                  href={`https://play.google.com/console/u/0/developers`}
                  target="_blank"
                  rel="noreferrer"
                >
                  Open Play Console
                </Button>
              ) : null}
            </Paper>
          ) : (
            <Box sx={{ mt: 1 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Validate the selected icon first, then publish it to the app's
                Google Play base locale.
              </Typography>

              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, mb: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Publish Target
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  App: {selectedApp?.name || "N/A"}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Package: {selectedAppPackageName || "Not configured"}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Base locale: {selectedApp?.googlePlayDefaultLocale || "en-US"}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Icon ID: {publishTargetIcon?.iconId || "N/A"}
                </Typography>
              </Paper>

              {publishBlockers.length > 0 ? (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  {publishBlockers.join(" ")}
                </Alert>
              ) : null}

              {iconValidationResult ? (
                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, mb: 2 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Validation Summary
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {iconValidationResult.message ||
                      "Validation completed for this icon."}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    Current Play icons in locale: {iconValidationResult.current.count}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Proposed size: {iconValidationResult.proposed.width || "?"}x
                    {iconValidationResult.proposed.height || "?"}
                  </Typography>
                  {iconValidationResult.validationIssues.length > 0 ? (
                    <Box sx={{ mt: 1 }}>
                      {iconValidationResult.validationIssues.map((issue, index) => (
                        <Alert
                          key={`${issue.field}-${index}`}
                          severity={issue.severity === "error" ? "error" : "warning"}
                          sx={{ mt: 1 }}
                        >
                          {issue.message}
                        </Alert>
                      ))}
                    </Box>
                  ) : (
                    <Alert severity="success" sx={{ mt: 1 }}>
                      Validation checks passed. You can publish this icon.
                    </Alert>
                  )}
                </Paper>
              ) : null}

              <FormControlLabel
                control={
                  <Checkbox
                    checked={publishConfirmed}
                    onChange={(event) => setPublishConfirmed(event.target.checked)}
                    disabled={publishBlockers.length > 0}
                  />
                }
                label="I reviewed validation results and want to publish this icon."
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closePublishDialog} disabled={publishingGooglePlayIcon}>
            {publishView === "success" ? "Done" : "Close"}
          </Button>
          {publishView === "form" ? (
            <>
              <Button
                variant="outlined"
                onClick={handleValidateGooglePlayIcon}
                disabled={
                  validatingGooglePlayIcon ||
                  publishingGooglePlayIcon ||
                  publishBlockers.length > 0
                }
                startIcon={
                  validatingGooglePlayIcon ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <CheckCircleIcon />
                  )
                }
              >
                {validatingGooglePlayIcon ? "Validating..." : "Validate"}
              </Button>
              <Button
                variant="contained"
                onClick={handlePublishGooglePlayIcon}
                disabled={!canPublishIcon}
                startIcon={
                  publishingGooglePlayIcon ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <PublishIcon />
                  )
                }
              >
                {publishingGooglePlayIcon ? "Publishing..." : "Publish to Google Play"}
              </Button>
            </>
          ) : null}
        </DialogActions>
      </Dialog>

      <Dialog
        open={cropDialogOpen}
        onClose={() => setCropDialogOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>Crop Icon and Generate Platform Assets</DialogTitle>
        <DialogContent>
          {!selectedIcon ? null : (
            <Grid container spacing={3} sx={{ mt: 0.5 }}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Crop Preview
                </Typography>
                <Box
                  sx={{
                    width: "100%",
                    maxWidth: 420,
                    aspectRatio: "1/1",
                    borderRadius: 3,
                    border: "2px dashed #90caf9",
                    overflow: "hidden",
                    bgcolor: "action.hover",
                    mx: "auto",
                  }}
                >
                  <canvas
                    ref={cropPreviewCanvasRef}
                    aria-label="Icon crop preview"
                    onPointerDown={handleCropPreviewPointerDown}
                    onPointerMove={handleCropPreviewPointerMove}
                    onPointerUp={handleCropPreviewPointerEnd}
                    onPointerCancel={handleCropPreviewPointerEnd}
                    onPointerLeave={handleCropPreviewPointerEnd}
                    style={{
                      width: "100%",
                      height: "100%",
                      display: "block",
                      cursor: isDraggingCropPreview ? "grabbing" : "grab",
                      touchAction: "none",
                    }}
                  />
                </Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", mt: 1 }}
                >
                  Adjust position and zoom to fit icon-safe area for
                  iOS/Android.
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth sx={{ mb: 3 }}>
                  <InputLabel>Variant Platform</InputLabel>
                  <Select
                    label="Variant Platform"
                    value={cropPlatform}
                    onChange={(e) =>
                      setCropPlatform(e.target.value as IconPlatform)
                    }
                  >
                    <MenuItem value="ios">iOS only</MenuItem>
                    <MenuItem value="android">Android only</MenuItem>
                    <MenuItem value="both">Both iOS + Android</MenuItem>
                  </Select>
                </FormControl>

                <Typography variant="body2">Horizontal Position</Typography>
                <Slider
                  min={-100}
                  max={100}
                  value={cropSettings.offsetX}
                  onChange={(_, value) =>
                    setCropSettings((prev) => ({
                      ...prev,
                      offsetX: Number(value),
                    }))
                  }
                />
                <Typography variant="body2">Vertical Position</Typography>
                <Slider
                  min={-100}
                  max={100}
                  value={cropSettings.offsetY}
                  onChange={(_, value) =>
                    setCropSettings((prev) => ({
                      ...prev,
                      offsetY: Number(value),
                    }))
                  }
                />
                <Typography variant="body2">Zoom</Typography>
                <Slider
                  min={1}
                  max={3}
                  step={0.05}
                  value={cropSettings.zoom}
                  onChange={(_, value) =>
                    setCropSettings((prev) => ({
                      ...prev,
                      zoom: Number(value),
                    }))
                  }
                />

                <Button
                  variant="contained"
                  sx={{ mt: 2 }}
                  onClick={handleGenerateVariants}
                  startIcon={
                    processingVariants ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : (
                      <ContentCutIcon />
                    )
                  }
                  disabled={processingVariants}
                >
                  {processingVariants
                    ? "Generating Variants..."
                    : "Generate All Size Variants"}
                </Button>
              </Grid>
            </Grid>
          )}

          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Generated Assets ({variants.length})
          </Typography>
          {variants.length === 0 ? (
            <Alert severity="info">No variants generated yet.</Alert>
          ) : (
            <Grid container spacing={2}>
              {variants.map((variant) => (
                <Grid
                  item
                  xs={12}
                  sm={6}
                  md={4}
                  lg={3}
                  key={`${variant.platform}-${variant.fileName}`}
                >
                  <Paper sx={{ p: 1.5 }}>
                    <Box
                      sx={{
                        width: "100%",
                        aspectRatio: "1/1",
                        mb: 1,
                        borderRadius: 1,
                        overflow: "hidden",
                        bgcolor: "action.hover",
                      }}
                    >
                      <img
                        src={variant.dataUrl}
                        alt={variant.fileName}
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                      />
                    </Box>
                    <Typography variant="caption" sx={{ display: "block" }}>
                      {variant.platform.toUpperCase()} - {variant.name}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: "block" }}
                    >
                      {variant.fileName}
                    </Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCropDialogOpen(false)}>Close</Button>
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            disabled={variants.length === 0 || !selectedApp}
            onClick={handleDownloadVariants}
          >
            Download Variant Assets
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
